/**
 * Row bookkeeping for the board.
 *
 * A row can reach the board twice over: once as the response to your own POST,
 * and again moments later when the Firestore snapshot listener reports the
 * write. Snapshots can also arrive late, so a stale one must never roll the
 * board backwards. Everything here is keyed on line_number, which the server
 * assigns and never reuses.
 */

function isRow(row) {
    return row !== null
        && typeof row === 'object'
        && Number.isInteger(row.line_number)
        && row.line_number > 0;
}

/**
 * Merges rows into the board, newest information winning per line number.
 *
 * Rows are only ever appended in this game, never edited away, so a snapshot
 * that is missing rows we already hold is stale rather than authoritative and
 * its omissions are ignored.
 */
export function mergeRows(existing = [], incoming = []) {
    const byLine = new Map();

    for (const row of existing) {
        if (isRow(row)) byLine.set(row.line_number, row);
    }
    for (const row of incoming) {
        if (isRow(row)) byLine.set(row.line_number, row);
    }

    return [...byLine.values()].sort((a, b) => a.line_number - b.line_number);
}

/** Reports whether the board already holds the given line number. */
export function hasRow(rows = [], lineNumber) {
    return rows.some((row) => isRow(row) && row.line_number === lineNumber);
}

/** The highest line number the board holds, or 0 when it is empty. */
export function lastLineNumber(rows = []) {
    return rows.reduce((max, row) => (isRow(row) && row.line_number > max ? row.line_number : max), 0);
}

/**
 * The reactions attached to a row, split into yours and the opponent's.
 *
 * The chat map is keyed by row *index*, not line number, so line 1 is key "0".
 * Mirrors models.ReactionKey.
 */
export function reactionsFor(chat, row, me) {
    const empty = { mine: '', theirs: '' };
    if (!chat || !isRow(row)) return empty;

    const entry = chat[String(row.line_number - 1)];
    if (!entry || typeof entry !== 'object') return empty;

    let mine = '';
    const theirs = [];
    for (const [uid, value] of Object.entries(entry)) {
        if (typeof value !== 'string' || value === '') continue;
        if (uid === me) mine = value;
        else theirs.push(value);
    }
    return { mine, theirs: theirs.join(' ') };
}

/** Whether a row carries any reaction at all. */
export function hasReaction(chat, row, me) {
    const { mine, theirs } = reactionsFor(chat, row, me);
    return mine !== '' || theirs !== '';
}

/**
 * Decides how a row should render: whose it is, and whether it won. Both
 * players guess at the same code, so an opponent's pegs light exactly like
 * yours and only the wire and score carry whose row it is. A cracked code is
 * gold whoever played it.
 */
export function rowMode(row, me, codeLength) {
    if (isRow(row) && row.result && row.result.placement === codeLength) return 'gold';
    return isRow(row) && row.user === me ? 'on' : 'theirs';
}
